import { motion } from 'framer-motion';
import { 
  Wifi, Shield, Activity, Cpu, Battery, Sun, 
  BarChart3, Lock, Gauge, CheckCircle2, ArrowRight 
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { CTABanner } from '../components/shared/CTABanner';
import { SectionHeader } from '../components/shared/SectionHeader';

const platformFeatures = [
  {
    icon: Activity,
    title: 'Real-Time Monitoring',
    description: 'Live visibility into charger status, session data, and energy consumption across all sites.'
  },
  {
    icon: Cpu,
    title: 'Remote Diagnostics',
    description: 'Identify and resolve issues remotely before they impact your operations.'
  },
  {
    icon: Gauge,
    title: 'Predictive Maintenance',
    description: 'Sensor analytics detect potential failures before they occur, minimizing downtime.'
  },
  {
    icon: BarChart3,
    title: 'Load Balancing',
    description: 'Advanced algorithms optimize power distribution across chargers to reduce peak demand.'
  },
  {
    icon: Lock,
    title: 'Secure Cloud Connectivity',
    description: 'Enterprise-grade security with encrypted communications and access controls.'
  },
  {
    icon: Battery,
    title: 'Energy Optimization',
    description: 'Smart scheduling and demand response integration to minimize energy costs.'
  }
];

const techSpecs = [
  {
    category: 'Interoperability',
    icon: Wifi,
    items: [
      'OCPP 1.6J and OCPP 2.0.1 compliant',
      'Hardware-agnostic platform',
      'Fleet management system integration',
      'Telematics API connectivity'
    ]
  },
  {
    category: 'Safety & Compliance',
    icon: Shield,
    items: [
      'NEC Article 625 compliant',
      'UL listed equipment',
      'ADA accessibility standards',
      'Utility interconnection standards'
    ]
  },
  {
    category: 'Sustainability',
    icon: Sun,
    items: [
      'Renewable energy integration',
      'Battery storage ready',
      'Demand response enabled',
      'Carbon tracking and reporting'
    ]
  }
];

const dashboardMetrics = [
  { label: 'Active Sessions', value: '1,247', change: '+12%' },
  { label: 'Energy Delivered', value: '45.2 MWh', change: '+8%' },
  { label: 'Network Uptime', value: '99.7%', change: '+0.2%' },
  { label: 'Avg. Charge Time', value: '42 min', change: '-5%' }
];

export const TechnologyPage = () => {
  return (
    <main className="pt-20" data-testid="technology-page">
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-slate-50 circuit-pattern" data-testid="technology-hero">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#00A2B5] mb-4 block">
              Technology & Approach
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-slate-900 mb-6">
              Smart Infrastructure,{' '}
              <span className="gradient-text">Intelligent Operations</span>
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Our intelligent charging platform integrates real-time diagnostics, load balancing, predictive maintenance, and energy optimization to ensure maximum uptime and operational efficiency.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Platform Features */}
      <section className="py-20 md:py-28 bg-white" data-testid="platform-features">
        <div className="container-custom">
          <SectionHeader
            overline="Platform Capabilities"
            title="High-Tech Charging Ecosystems"
            centered
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {platformFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-xl p-8 border border-slate-200 card-hover"
                data-testid={`feature-${index}`}
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#00A2B5]/10 to-[#36D890]/10 flex items-center justify-center mb-6">
                  <feature.icon className="w-7 h-7 text-[#00A2B5]" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-600 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Dashboard Preview */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="dashboard-preview">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <SectionHeader
                overline="Data Dashboards"
                title="Real-Time Visibility"
                subtitle="Our intuitive dashboards provide fleet managers and site hosts with actionable insights into charging performance, energy consumption, and operational metrics."
              />

              <ul className="space-y-4">
                {[
                  'Live charger status and session monitoring',
                  'Historical energy and usage analytics',
                  'Customizable alerts and notifications',
                  'Multi-site portfolio management',
                  'Exportable reports for stakeholders'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#00C5A5] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden"
            >
              {/* Mock Dashboard */}
              <div className="bg-slate-900 p-4 flex items-center gap-2">
                <div className="flex gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-red-500" />
                  <span className="w-3 h-3 rounded-full bg-yellow-500" />
                  <span className="w-3 h-3 rounded-full bg-green-500" />
                </div>
                <span className="text-slate-400 text-sm ml-4">Skytec EV Dashboard</span>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-2 gap-4 mb-6">
                  {dashboardMetrics.map((metric, idx) => (
                    <div key={idx} className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                      <p className="text-xs text-slate-500 mb-1">{metric.label}</p>
                      <div className="flex items-end gap-2">
                        <p className="text-2xl font-black text-slate-900">{metric.value}</p>
                        <span className={`text-xs font-semibold ${metric.change.startsWith('+') ? 'text-green-600' : 'text-red-600'}`}>
                          {metric.change}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Mini chart placeholder */}
                <div className="bg-gradient-to-r from-[#00A2B5]/10 to-[#36D890]/10 rounded-xl h-32 flex items-center justify-center border border-slate-100">
                  <div className="flex items-end gap-1 h-20">
                    {[40, 65, 45, 80, 55, 90, 70, 85].map((h, i) => (
                      <div
                        key={i}
                        className="w-6 bg-gradient-to-t from-[#00A2B5] to-[#36D890] rounded-t-sm opacity-70"
                        style={{ height: `${h}%` }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="py-20 md:py-28 bg-white" data-testid="tech-specs">
        <div className="container-custom">
          <SectionHeader
            overline="Technical Standards"
            title="Built for Enterprise"
            centered
          />

          <div className="grid md:grid-cols-3 gap-8">
            {techSpecs.map((spec, index) => (
              <motion.div
                key={spec.category}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-slate-50 rounded-xl p-8 border border-slate-200"
                data-testid={`spec-${index}`}
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00A2B5] to-[#36D890] flex items-center justify-center mb-6 shadow-lg">
                  <spec.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-4">{spec.category}</h3>
                <ul className="space-y-3">
                  {spec.items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-slate-600">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#00A2B5] mt-2 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Future Ready Section */}
      <section className="py-20 md:py-28 bg-slate-900" data-testid="future-ready">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#00C5A5] mb-4 block">
              Future-Ready
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tight text-white mb-6">
              Built for Tomorrow's Grid
            </h2>
            <p className="text-xl text-slate-300 mb-10 leading-relaxed">
              Our infrastructure is designed to integrate with renewable energy sources, battery storage systems, and vehicle-to-grid (V2G) technology as these capabilities become available.
            </p>
            <Link
              to="/contact"
              className="btn-gradient inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-lg group"
              data-testid="future-ready-cta"
            >
              Discuss Your Technology Needs
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Banner */}
      <CTABanner variant="light" />
    </main>
  );
};

export default TechnologyPage;
